import { takeLatest, call, select, put, all, takeEvery, throttle } from 'redux-saga/effects';
import { URLS } from '@wix/da-url';
import { toggleMature, updateContentFilter, switchVersion, toggleTheme, toggleThemeDone, updateCommentsOrder, updateCommentsOrderDone, resendVerificationEmail, updateEmail, resendVerificationEmailFail, resendVerificationEmailSuccess, updateEmailFail, updateEmailSuccess, setViewMode, setThemeSkin, setTheme, fetchFeedbackCount, setFeedbackCount, setCookieSettingsSuccess, setCookieSettingsFail, setCookieAccepted, setCookieSettings, setAllCookiesAccepted, setCookieAcceptedAndReturn } from './actions';
import { getTheme, getVerificationEmailAddress, getIsLoggedIn, getThemeSkin, getCookieSettings } from './selectors';
import { request } from '../xhr';
import { openUserDropdown } from '../SiteHeader/redux/actions';
import { DeviationViewMode } from '@wix/da-react-context/pkg/SessionContext';
import { ThemeSkin } from '@wix/da-react-context/pkg/ThemeContext';
import { restrictLoggedOut } from '../redux/loggedout';
import ddt from '@wix/da-ddt';
import { ReferralType } from '@wix/da-bi/pkg/constants';
const logger = ddt.createLogger('publicSession');
const FETCH_FEEDBACK_COUNT_WAIT = 5000;
function* toggleMatureHandler(action) {
    if (!action.payload) {
        return;
    }
    const isLoggedIn = yield select(getIsLoggedIn);
    if (!isLoggedIn) {
        yield put(restrictLoggedOut({
            restrictedActionName: ReferralType.MATURE
        }));
        return;
    }
    const { current  } = action.payload;
    const result = yield call(request, {
        method: 'post',
        url: '/user/settings/mature',
        data: {
            flag: !current
        }
    }, ()=>({
            success: true
        }));
    if (result && result.success) {
        window.location.reload();
    } else {
        if (result.error_code === 0) {
            alert('Error, you are too young to enable mature content');
            return;
        }
        alert('Failed to save mature setting');
    }
}
function* updateContentFilterHandler(action) {
    if (!action.payload) {
        return;
    }
    const isLoggedIn = yield select(getIsLoggedIn);
    if (!isLoggedIn) {
        yield put(restrictLoggedOut({
            restrictedActionName: ReferralType.MATURE
        }));
        return;
    }
    const { contentFilter  } = action.payload;
    const result = yield call(request, {
        method: 'post',
        url: '/user/settings/content_filter',
        data: {
            content_filter: contentFilter
        }
    }, ()=>({
            success: true
        }));
    if (result && result.success) {
        window.location.reload();
    } else {
        alert([
            'Failed to save setting:',
            `  setting: ${contentFilter}`,
            `  ${result.errorDescription}`
        ].join('\n'));
    }
}
function* switchVersionHandler(action) {
    if (!action.payload) {
        return;
    }
    const result = yield call(request, {
        method: 'post',
        url: '/user/settings/version',
        data: {
            is_da_version_9_1: true
        }
    }, ()=>({
            success: true
        }));
    if (result && result.success) {
        window.location.reload();
    } else {
        alert('Failed to switch version please try again');
    }
}
function* saveThemeHandler() {
    // Reducer will have updated it
    const currentTheme = yield select(getTheme);
    const currentThemeSkin = yield select(getThemeSkin);
    let themeToSet = currentTheme;
    if (currentThemeSkin === ThemeSkin.GREEN) {
        themeToSet = 'green';
    }
    const result = yield call(request, {
        method: 'post',
        url: '/user/theme',
        data: {
            theme: themeToSet
        }
    }, ()=>({
            success: true
        }));
    if (!result || !result.success) {
        alert('Failed to save theme setting');
        return;
    }
    yield put(toggleThemeDone());
}
function* updateCommentsOrderHandler({ payload  }) {
    if (!payload) {
        return;
    }
    const data = {
        order: payload.order
    };
    const result = yield call(request, {
        method: 'post',
        url: '/user/settings/comments_order',
        data
    }, ()=>({
            success: true
        }));
    if (!result || !result.success) {
        alert('Failed to save comments order');
        return;
    }
    yield put(updateCommentsOrderDone(payload.order));
}
function* resendVerificationEmailHandler() {
    yield put(openUserDropdown());
    const data = {};
    const result = yield call(request, {
        method: 'post',
        url: '/user/resend_verification_email',
        data
    }, ()=>({
            success: true
        }));
    if (!result || !result.success) {
        alert('Failed to resend email');
        yield put(resendVerificationEmailFail());
        return;
    }
    yield put(resendVerificationEmailSuccess());
}
function* updateEmailHandler({ payload  }) {
    if (!payload) {
        return;
    }
    const { email  } = payload;
    const currentEmail = yield select(getVerificationEmailAddress);
    // it's as good as done so whatever
    if (email === currentEmail) {
        yield put(updateEmailSuccess(email));
        return;
    }
    const data = {
        email
    };
    const result = yield call(request, {
        method: 'post',
        url: '/user/update_email',
        data
    }, ()=>({
            success: true
        }));
    if (!result || !result.success) {
        yield put(updateEmailFail(email));
        return;
    }
    yield put(updateEmailSuccess(email));
}
function* setAllCookiesAcceptedHandler() {
    yield put(setCookieSettings({
        functional: true,
        marketing: true,
        analytics: true
    }));
    yield put(setCookieAccepted());
}
function* setCookieAcceptedHandler() {
    const types = yield select(getCookieSettings);
    const data = {
        accepted: true,
        type_functional: types.functional,
        type_marketing: types.marketing,
        type_analytics: types.analytics
    };
    const result = yield call(request, {
        method: 'post',
        url: '/user/cookiesettings/save',
        data
    }, ()=>({
            success: true
        }));
    if (!result || !result.success) {
        yield put(setCookieSettingsFail());
        return;
    }
    yield put(setCookieSettingsSuccess());
}
function* setCookieAcceptedAndReturnHandler() {
    yield takeLatest(setCookieSettingsSuccess, ()=>window.location.assign(URLS.PRIVACY));
    yield call(setCookieAcceptedHandler);
}
function* setViewModeHandler({ payload: { nextViewMode  }  }) {
    // only save normal/theater, fullscreen is irrelevant
    if (nextViewMode === DeviationViewMode.Fullscreen) {
        return;
    }
    const data = {
        view_mode: nextViewMode
    };
    const result = yield call(request, {
        method: 'post',
        url: 'deviation/set_view_mode',
        data
    }, ()=>({
            success: true
        }));
    if (!result || !result.success) {
        logger.error('Failed to persist new view mode', nextViewMode);
    }
}
function* fetchFeedbackCountHandler() {
    const result = yield call(request, {
        method: 'get',
        url: `/unread`
    }, ()=>({
            counts: {
                feedback: 1
            }
        }));
    if (result && !result.error) {
        var _result_counts;
        var _result_counts_feedback;
        yield put(setFeedbackCount((_result_counts_feedback = result === null || result === void 0 ? void 0 : (_result_counts = result.counts) === null || _result_counts === void 0 ? void 0 : _result_counts.feedback) !== null && _result_counts_feedback !== void 0 ? _result_counts_feedback : 0));
    }
}
export default function* saga() {
    yield all([
        takeLatest(switchVersion, switchVersionHandler),
        takeLatest(toggleMature, toggleMatureHandler),
        takeLatest(updateContentFilter, updateContentFilterHandler),
        takeLatest(toggleTheme, saveThemeHandler),
        takeLatest(setTheme, saveThemeHandler),
        takeLatest(setThemeSkin, saveThemeHandler),
        takeLatest(updateCommentsOrder, updateCommentsOrderHandler),
        takeLatest(resendVerificationEmail, resendVerificationEmailHandler),
        takeLatest(updateEmail, updateEmailHandler),
        takeEvery(setViewMode, setViewModeHandler),
        takeEvery(setCookieAccepted, setCookieAcceptedHandler),
        takeEvery(setCookieAcceptedAndReturn, setCookieAcceptedAndReturnHandler),
        takeEvery(setAllCookiesAccepted, setAllCookiesAcceptedHandler),
        throttle(FETCH_FEEDBACK_COUNT_WAIT, fetchFeedbackCount, fetchFeedbackCountHandler)
    ]);
}
